import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api.js';

export interface Loaded<T> {
  readonly value: T | null;
  readonly error: string | null;
  readonly loading: boolean;
  readonly reload: () => void;
  readonly set: (value: T) => void;
}

/**
 * Reads one resource and keeps it. A failure becomes a sentence the operator can read,
 * never a blank screen: an empty surface and a broken surface must not look alike.
 */
export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): Loaded<T> {
  const [value, setValue] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const run = useCallback(load, deps);

  useEffect(() => {
    let live = true;
    setLoading(true);
    run()
      .then((next) => {
        if (!live) return;
        setValue(next);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (!live) return;
        setError(caught instanceof ApiError ? caught.message : 'Не удалось связаться со Studio.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [run, nonce]);

  return {
    value,
    error,
    loading,
    reload: () => setNonce((n) => n + 1),
    set: (next: T) => setValue(next),
  };
}
